/**
 * OrderEditView Component - Form for editing orders
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { ArrowBack, Save } from "@mui/icons-material";
import {
  getOrder,
  getOrderCompletionEligibility,
  updateOrder,
  updateOrderStatus,
} from "../services/orderService";
import { logError, logInfo, logWarn } from "../../../shared/utils/logger.js";
import StatusChangeConfirmationModal from "../../drops/components/StatusChangeConfirmationModal";
import OrderFieldChangesConfirmationModal from "../components/OrderFieldChangesConfirmationModal";
import { fetchCustomerEmail } from "../services/customerEmailService";

/**
 * OrderEditView Component
 */
const OrderEditView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [order, setOrder] = useState(null);

  const [formData, setFormData] = useState({
    customer: "",
    customerPhone: "",
    pickup: "",
    delivery: "",
    status: "pending",
    priority: "medium",
    estimatedDelivery: "",
    notes: "",
  });

  // Modal states
  const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
  const [fieldChangesModalOpen, setFieldChangesModalOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [statusChangeData, setStatusChangeData] = useState(null);
  const [fieldChangesData, setFieldChangesData] = useState(null);

  // Load order data
  const loadOrder = useCallback(async () => {
    if (!orderId) {
      setError("Invalid order identifier");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const orderData = await getOrder(orderId);

      // Handle different response structures
      let order =
        orderData?.data?.data || orderData?.data || orderData || null;

      if (!order || !order.id) {
        throw new Error("Order data not found");
      }

      // If customerEmail is missing, try to fetch it with fallback to name/phone search
      if (!order.customerEmail && order.customerId) {
        try {
          const customerEmail = await fetchCustomerEmail(
            order.customerId,
            order.customer, // Pass customer name for fallback
            order.customerPhone // Pass customer phone for fallback
          );
          if (customerEmail) {
            order = { ...order, customerEmail };
          }
        } catch (err) {
          logWarn("Failed to fetch customer email in OrderEditView", {
            customerId: order.customerId,
            customerName: order.customer,
            customerPhone: order.customerPhone,
            error: err.message,
          });
        }
      }

      setOrder(order);

      // Populate form with order data
      setFormData({
        customer: order.customer || "",
        customerPhone: order.customerPhone || "",
        pickup: order.pickup?.address || order.pickup || "",
        delivery: order.delivery?.address || order.delivery || "",
        status: order.status || "pending",
        priority: order.priority || "medium",
        estimatedDelivery: order.estimatedDelivery
          ? new Date(order.estimatedDelivery).toISOString().split("T")[0]
          : "",
        notes: order.notes || "",
      });

      // Debug logging
      logInfo("OrderEditView: order loaded", {
        orderId,
        hasCustomerEmail: !!order.customerEmail,
        customerEmail: order.customerEmail || "NOT FOUND",
        customerId: order.customerId,
      });
    } catch (err) {
      logError("OrderEditView: failed to load order", err);
      setError(err.message || "Failed to load order");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    setError(null);
  };

  // Compute field changes
  const computeChanges = () => {
    if (!order) return { statusChanges: {}, fieldChanges: {} };

    const statusChanges = {};
    const fieldChanges = {};

    // Check status change
    if (order.status !== formData.status) {
      statusChanges[orderId] = {
        oldStatus: order.status,
        newStatus: formData.status,
        order: order,
        customerEmail: order.customerEmail || null,
        customerName: order.customer || formData.customer,
      };
    }

    // Check other field changes
    const fieldLabels = {
      customer: "Customer Name",
      customerPhone: "Customer Phone",
      pickup: "Pickup Address",
      delivery: "Delivery Address",
      priority: "Priority",
      estimatedDelivery: "Estimated Delivery",
      notes: "Notes",
    };

    // Customer
    if (order.customer !== formData.customer) {
      fieldChanges.customer = {
        oldValue: order.customer,
        newValue: formData.customer,
        label: fieldLabels.customer,
      };
    }

    // Customer Phone
    if (order.customerPhone !== formData.customerPhone) {
      fieldChanges.customerPhone = {
        oldValue: order.customerPhone,
        newValue: formData.customerPhone,
        label: fieldLabels.customerPhone,
      };
    }

    // Pickup
    const currentPickup = order.pickup?.address || order.pickup || "";
    if (currentPickup !== formData.pickup) {
      fieldChanges.pickup = {
        oldValue: currentPickup,
        newValue: formData.pickup,
        label: fieldLabels.pickup,
      };
    }

    // Delivery
    const currentDelivery = order.delivery?.address || order.delivery || "";
    if (currentDelivery !== formData.delivery) {
      fieldChanges.delivery = {
        oldValue: currentDelivery,
        newValue: formData.delivery,
        label: fieldLabels.delivery,
      };
    }

    // Priority
    if (order.priority !== formData.priority) {
      fieldChanges.priority = {
        oldValue: order.priority,
        newValue: formData.priority,
        label: fieldLabels.priority,
      };
    }

    // Estimated Delivery
    const currentEstimatedDelivery = order.estimatedDelivery
      ? new Date(order.estimatedDelivery).toISOString().split("T")[0]
      : "";
    if (currentEstimatedDelivery !== formData.estimatedDelivery) {
      fieldChanges.estimatedDelivery = {
        oldValue: order.estimatedDelivery
          ? new Date(order.estimatedDelivery).toLocaleDateString()
          : "Not set",
        newValue: formData.estimatedDelivery
          ? new Date(formData.estimatedDelivery).toLocaleDateString()
          : "Not set",
        label: fieldLabels.estimatedDelivery,
      };
    }

    // Notes
    if ((order.notes || "") !== (formData.notes || "")) {
      fieldChanges.notes = {
        oldValue: order.notes || "Not set",
        newValue: formData.notes || "Not set",
        label: fieldLabels.notes,
      };
    }

    return { statusChanges, fieldChanges };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate required fields
    if (!formData.customer) {
      setError("Customer name is required");
      return;
    }

    if (!formData.customerPhone) {
      setError("Customer phone is required");
      return;
    }

    if (!formData.pickup) {
      setError("Pickup address is required");
      return;
    }

    if (!formData.delivery) {
      setError("Delivery address is required");
      return;
    }

    // Prepare update data
    const updateData = {
      customer: formData.customer,
      customerPhone: formData.customerPhone,
      pickup: {
        address: formData.pickup,
      },
      delivery: {
        address: formData.delivery,
      },
      status: formData.status,
      priority: formData.priority,
      estimatedDelivery: formData.estimatedDelivery
        ? new Date(formData.estimatedDelivery).toISOString()
        : null,
      notes: formData.notes || "",
    };

    // Compute changes
    const { statusChanges, fieldChanges } = computeChanges();
    const hasStatusChange = Object.keys(statusChanges).length > 0;
    const hasFieldChanges = Object.keys(fieldChanges).length > 0;

    // If no changes, show error
    if (!hasStatusChange && !hasFieldChanges) {
      setError("No changes detected");
      return;
    }

    // Store pending update data
    setPendingUpdateData(updateData);

    // Show appropriate modal
    if (hasStatusChange) {
      setStatusChangeData(statusChanges);
      setStatusChangeModalOpen(true);
    } else if (hasFieldChanges) {
      setFieldChangesData(fieldChanges);
      setFieldChangesModalOpen(true);
    }
  };

  // Handle confirmation from status change modal
  const handleStatusChangeConfirm = async (
    emailPreferences,
    options = {}
  ) => {
    setStatusChangeModalOpen(false);
    await executeUpdate(emailPreferences, options);
  };

  // Handle confirmation from field changes modal
  const handleFieldChangesConfirm = async () => {
    setFieldChangesModalOpen(false);
    await executeUpdate(null);
  };

  // Execute the update
  const executeUpdate = async (emailPreferences, options = {}) => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData = pendingUpdateData;
      const { statusChanges, fieldChanges } = computeChanges();
      const hasStatusChange = Object.keys(statusChanges).length > 0;
      const hasFieldChanges = Object.keys(fieldChanges).length > 0;

      if (hasStatusChange) {
        // Update status separately with email context
        const context = {};
        if (emailPreferences && !emailPreferences[orderId]) {
          context.skipEmail = true;
        }
        if (updateData.status === "completed") {
          context.includeReviewRequest =
            !context.skipEmail && !!options.includeReviewRequest;
        }

        if (updateData.status === "completed") {
          const eligibility = await getOrderCompletionEligibility(orderId);
          if (eligibility && eligibility.canComplete === false) {
            throw new Error(
              eligibility.message ||
                "Job completion blocked: resolve blockers first."
            );
          }
        }

        await updateOrderStatus(orderId, updateData.status, context);

        // Update other fields if any
        const otherUpdates = { ...updateData };
        delete otherUpdates.status;

        if (Object.keys(otherUpdates).length > 0) {
          await updateOrder(orderId, otherUpdates);
        }
      } else if (hasFieldChanges) {
        // Regular update (no status change)
        // Remove status field to avoid backend rejection
        const { status, ...fieldUpdates } = updateData;
        await updateOrder(orderId, fieldUpdates);
      }

      setSuccess(true);
      logInfo("OrderEditView: order updated", { orderId });

      setTimeout(() => {
        navigate(`/orders/${orderId}`);
      }, 1500);
    } catch (err) {
      logError("OrderEditView: failed to update order", err);
      setError(err.message || "Failed to update order");
    } finally {
      setSaving(false);
      setPendingUpdateData(null);
      setStatusChangeData(null);
      setFieldChangesData(null);
    }
  };

  const handleBack = useCallback(() => {
    const previous = location.state?.from;
    if (previous?.pathname) {
      navigate(`${previous.pathname}${previous.search || ""}`);
    } else if (typeof previous === "string") {
      navigate(previous);
    } else {
      navigate(`/orders/${orderId}`);
    }
  }, [navigate, location.state, orderId]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !order) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4">Edit Order {orderId}</Typography>
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Order updated successfully! Redirecting...
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Order Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Order Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Order ID"
                  value={order?.id || ""}
                  disabled
                  helperText="Order ID cannot be changed"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  select
                  value={formData.status}
                  onChange={handleChange("status")}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="arrived_at_storage">Arrived at Storage</MenuItem>
                  <MenuItem value="in_transit_from_storage">In Transit from Storage</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Priority"
                  select
                  value={formData.priority}
                  onChange={handleChange("priority")}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estimated Delivery"
                  type="date"
                  value={formData.estimatedDelivery}
                  onChange={handleChange("estimatedDelivery")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={formData.customer}
                  onChange={handleChange("customer")}
                  required
                  error={error && !formData.customer}
                  helperText={
                    error && !formData.customer
                      ? "Customer name is required"
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Phone *"
                  value={formData.customerPhone}
                  onChange={handleChange("customerPhone")}
                  required
                  error={error && !formData.customerPhone}
                  helperText={
                    error && !formData.customerPhone
                      ? "Customer phone is required"
                      : ""
                  }
                />
              </Grid>

              {/* Route Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Route Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pickup Address *"
                  value={formData.pickup}
                  onChange={handleChange("pickup")}
                  multiline
                  rows={2}
                  required
                  error={error && !formData.pickup}
                  helperText={
                    error && !formData.pickup
                      ? "Pickup address is required"
                      : ""
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Delivery Address *"
                  value={formData.delivery}
                  onChange={handleChange("delivery")}
                  multiline
                  rows={2}
                  required
                  error={error && !formData.delivery}
                  helperText={
                    error && !formData.delivery
                      ? "Delivery address is required"
                      : ""
                  }
                />
              </Grid>

              {/* Additional Notes */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                  placeholder="Additional notes about this order..."
                />
              </Grid>

              {/* Items Display (Read-only) */}
              {order?.items && order.items.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Items
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "grey.50",
                        borderRadius: 1,
                      }}
                    >
                      {order.items.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 1,
                            p: 1,
                            backgroundColor: "white",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body1">
                            {item.quantity ? <strong>{item.quantity}x </strong> : ""}
                            {item.description || item.content || `Item ${index + 1}`}
                          </Typography>
                          {item.type && item.type !== "text" && (
                            <Typography variant="body2" color="text.secondary">
                              Type: {item.type}
                            </Typography>
                          )}
                          {item.weight && (
                            <Typography variant="body2" color="text.secondary">
                              Weight: {item.weight} kg
                            </Typography>
                          )}
                        </Box>
                      ))}
                      {order.totalWeight && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            backgroundColor: "primary.light",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            Total Weight: {order.totalWeight} kg
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </>
              )}

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      saving ? <CircularProgress size={20} /> : <Save />
                    }
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Update Order"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Status Change Confirmation Modal */}
      <StatusChangeConfirmationModal
        open={statusChangeModalOpen}
        statusChanges={statusChangeData}
        onConfirm={handleStatusChangeConfirm}
        onCancel={() => {
          setStatusChangeModalOpen(false);
          setPendingUpdateData(null);
          setStatusChangeData(null);
        }}
        loading={saving}
      />

      {/* Field Changes Confirmation Modal */}
      <OrderFieldChangesConfirmationModal
        open={fieldChangesModalOpen}
        order={order}
        changes={fieldChangesData}
        onConfirm={handleFieldChangesConfirm}
        onCancel={() => {
          setFieldChangesModalOpen(false);
          setPendingUpdateData(null);
          setFieldChangesData(null);
        }}
        loading={saving}
      />
    </Box>
  );
};

export default OrderEditView;

